import DateRangePicker, { DateRange } from 'common/components/DateRangePicker'
import { useFarms } from 'modules/farm/api/queries'
import { formatThaiShortBE } from 'modules/inventory/helper/format_date'
import { useGetCyclesByFarmByPond, useGetPondsByFarm } from 'modules/pond/api/queries'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

// ---------- types ----------

export type ScopeType = 'all' | 'Farm' | 'Pond' | 'Cycle'
export type DatePreset = 'none' | 'today' | '7days' | '14days' | 'custom'

export interface FilterState {
  scopeType: ScopeType
  scopeFarmId: string
  scopePondId: string
  scopeCycleId: string
  datePreset: DatePreset
  customRange: DateRange
}

export const DEFAULT_FILTER: FilterState = {
  scopeType: 'all',
  scopeFarmId: '',
  scopePondId: '',
  scopeCycleId: '',
  datePreset: 'none',
  customRange: { startDate: null, endDate: null },
}

export function computeDateRange(preset: DatePreset, custom: DateRange): { startDate?: string; endDate?: string } {
  if (preset === 'none') return {}
  const now = new Date()
  const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0)

  if (preset === 'today') {
    return { startDate: startOfToday.toISOString(), endDate: endOfToday.toISOString() }
  }
  if (preset === '7days') {
    const s = new Date(startOfToday); s.setDate(s.getDate() - 7)
    return { startDate: s.toISOString(), endDate: endOfToday.toISOString() }
  }
  if (preset === '14days') {
    const s = new Date(startOfToday); s.setDate(s.getDate() - 14)
    return { startDate: s.toISOString(), endDate: endOfToday.toISOString() }
  }
  if (preset === 'custom' && custom.startDate && custom.endDate) {
    return { startDate: custom.startDate.toISOString(), endDate: custom.endDate.toISOString() }
  }
  return {}
}

export function filterToApiBase(filter: FilterState) {
  const scopePart: { scopeFilter?: 'Farm' | 'Pond' | 'Cycle'; scopeValue?: string } = {}
  if (filter.scopeType === 'Farm' && filter.scopeFarmId) {
    scopePart.scopeFilter = 'Farm'; scopePart.scopeValue = filter.scopeFarmId
  } else if (filter.scopeType === 'Pond' && filter.scopePondId) {
    scopePart.scopeFilter = 'Pond'; scopePart.scopeValue = filter.scopePondId
  } else if (filter.scopeType === 'Cycle' && filter.scopeCycleId) {
    scopePart.scopeFilter = 'Cycle'; scopePart.scopeValue = filter.scopeCycleId
  }
  return { ...scopePart, ...computeDateRange(filter.datePreset, filter.customRange) }
}

// ---------- component ----------

interface FilterDialogProps {
  open: boolean
  onClose: () => void
  value: FilterState
  onConfirm: (f: FilterState) => void
}

function FilterDialog({ open, onClose, value, onConfirm }: FilterDialogProps) {
  const { t } = useTranslation('stock')
  const [draft, setDraft] = useState<FilterState>(value)
  const { data: farmsData } = useFarms()
  const farms = React.useMemo(() => {
    if (Array.isArray(farmsData)) return farmsData as any[]
    if (Array.isArray((farmsData as any)?.farms)) return (farmsData as any).farms as any[]
    return []
  }, [farmsData])

  const { data: ponds = [] } = useGetPondsByFarm(draft.scopeFarmId)
  const { data: cycles = [] } = useGetCyclesByFarmByPond(draft.scopeFarmId, draft.scopePondId)

  useEffect(() => { if (open) setDraft(value) }, [open, value])

  const scopeChips: { key: ScopeType; labelKey: string }[] = [
    { key: 'all', labelKey: 'filter.scope.all' },
    { key: 'Farm', labelKey: 'filter.scope.farm' },
    { key: 'Pond', labelKey: 'filter.scope.pond' },
    { key: 'Cycle', labelKey: 'filter.scope.cycle' },
  ]
  const dateChips: { key: DatePreset; labelKey: string }[] = [
    { key: 'today', labelKey: 'filter.today' },
    { key: '7days', labelKey: 'filter.7days' },
    { key: '14days', labelKey: 'filter.14days' },
    { key: 'custom', labelKey: 'filter.custom' },
  ]

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>{t('filter.title')}</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>{t('filter.display')}</Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          {scopeChips.map((c) => (
            <Chip
              key={c.key}
              label={t(c.labelKey)}
              onClick={() => setDraft((d) => ({ ...d, scopeType: c.key, scopeFarmId: '', scopePondId: '', scopeCycleId: '' }))}
              sx={{
                borderRadius: 5,
                fontWeight: 600,
                bgcolor: draft.scopeType === c.key ? 'primary.main' : 'grey.100',
                color: draft.scopeType === c.key ? 'common.white' : 'text.primary',
                '&:hover': { bgcolor: draft.scopeType === c.key ? 'primary.dark' : 'grey.200' },
              }}
            />
          ))}
        </Stack>

        {(draft.scopeType === 'Farm' || draft.scopeType === 'Pond' || draft.scopeType === 'Cycle') && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              <span style={{ color: 'red', marginRight: 4 }}>*</span>{t('filter.farm.label')}
            </Typography>
            <TextField
              select fullWidth size="small"
              value={draft.scopeFarmId}
              onChange={(e) => setDraft((d) => ({ ...d, scopeFarmId: e.target.value, scopePondId: '', scopeCycleId: '' }))}
              SelectProps={{ displayEmpty: true, renderValue: (v) => v ? farms.find((f: any) => String(f.id) === String(v))?.name ?? '' : <span style={{ opacity: 0.6 }}>{t('filter.farm.placeholder')}</span> }}
            >
              {farms.map((f: any) => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}
            </TextField>
          </Box>
        )}

        {(draft.scopeType === 'Pond' || draft.scopeType === 'Cycle') && draft.scopeFarmId && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              <span style={{ color: 'red', marginRight: 4 }}>*</span>{t('filter.pond.label')}
            </Typography>
            <TextField
              select fullWidth size="small"
              value={draft.scopePondId}
              onChange={(e) => setDraft((d) => ({ ...d, scopePondId: e.target.value, scopeCycleId: '' }))}
              SelectProps={{ displayEmpty: true, renderValue: (v) => v ? (ponds as any[]).find((p) => String(p.id) === String(v))?.name ?? '' : <span style={{ opacity: 0.6 }}>{t('filter.pond.placeholder')}</span> }}
            >
              {(ponds as any[]).map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </TextField>
          </Box>
        )}

        {draft.scopeType === 'Cycle' && draft.scopeFarmId && draft.scopePondId && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              <span style={{ color: 'red', marginRight: 4 }}>*</span>{t('filter.cycle.label')}
            </Typography>
            <TextField
              select fullWidth size="small"
              value={draft.scopeCycleId}
              onChange={(e) => setDraft((d) => ({ ...d, scopeCycleId: e.target.value }))}
              SelectProps={{
                displayEmpty: true, renderValue: (v) => {
                  if (!v) return <span style={{ opacity: 0.6 }}>{t('filter.cycle.placeholder')}</span>
                  const c = (cycles as any[]).find((x) => String(x.id) === String(v))
                  return c ? `${formatThaiShortBE(c.startDate)} - ${c.endDate ? formatThaiShortBE(c.endDate) : t('filter.cycle.current')}` : ''
                }
              }}
            >
              {(cycles as any[]).map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {formatThaiShortBE(c.startDate)} - {c.endDate ? formatThaiShortBE(c.endDate) : t('filter.cycle.current')}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>{t('filter.period')}</Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          {dateChips.map((c) => (
            <Chip
              key={c.key}
              label={t(c.labelKey)}
              onClick={() => setDraft((d) => ({ ...d, datePreset: c.key }))}
              sx={{
                borderRadius: 5,
                fontWeight: 600,
                bgcolor: draft.datePreset === c.key ? 'primary.main' : 'grey.100',
                color: draft.datePreset === c.key ? 'common.white' : 'text.primary',
                '&:hover': { bgcolor: draft.datePreset === c.key ? 'primary.dark' : 'grey.200' },
              }}
            />
          ))}
        </Stack>

        {draft.datePreset === 'custom' && (
          <Box sx={{ mb: 1 }}>
            <DateRangePicker
              value={draft.customRange}
              onChange={(r) => setDraft((d) => ({ ...d, customRange: r }))}
              showLabel={false}
            />
          </Box>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" color="inherit" onClick={() => { setDraft(DEFAULT_FILTER); onConfirm(DEFAULT_FILTER); onClose() }}>
          {t('filter.clear')}
        </Button>
        <Button variant="contained" onClick={() => { onConfirm(draft); onClose() }}>{t('dialog.stock.confirm')}</Button>
      </DialogActions>
    </Dialog>
  )
}

export default FilterDialog
